import React from 'react';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import Utilities from '@util/Utilities';
import Currency from '@app/model/_common/Currency';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import { AvailabilityDailyStateContext } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import DerivativePriceSynchronizer
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/DerivativePriceSynchronizer';
import { DerivativePriceRule } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/DerivativePriceSynchronization.utilities';

export type PriceEditRoom = {
	roomId: number,
	roomName: string,
	isAnchor: boolean,
	currentPrice: number,
	// The rule of a derivative room; undefined for the master room and for a room with no rule.
	derivativeRule?: DerivativePriceRule,
	// The index of the room in `rates[rateIndex].roomPrices` of the form.
	roomIndex: number,
};

export type PriceEditGroup = {
	key: string,
	// The index of the group in `rates` of the form.
	rateIndex: number,
	roomMappingType: string,
	rooms: PriceEditRoom[],
};

type PriceEditRoomsTableProps = {
	title: string,
	currency: Currency,
	groups: PriceEditGroup[],
	// The TanStack form of the modal. Its values hold `rates[rateIndex].roomPrices[roomIndex].submittedPrice`.
	form: any,
	// A bump stops the master-to-derivative cascade for that change (Reset, Bulk Adjustment).
	suppressionEpoch: number,
};

const renderRuleNote = (room: PriceEditRoom, currency: Currency, roomType: string): React.ReactNode => {
	if (room.isAnchor) return `Base Price - ${roomType}`;
	const rule = room.derivativeRule;
	if (rule === undefined) return `Derived from base price - ${roomType}`;
	if (rule.type === PricingArchitectureDerivativeRuleType.SAME) return `Equal to base price - ${roomType}`;
	const value = rule.value ?? 0;
	const sign = Number(value) > 0 ? '+' : '';
	if (rule.type === PricingArchitectureDerivativeRuleType.AMOUNT) {
		return `${sign}${value}${Utilities.formatCurrency(currency)} from base price - ${roomType}`;
	}
	if (rule.type === PricingArchitectureDerivativeRuleType.PERCENTAGE) {
		return `${sign}${value}% from base price - ${roomType}`;
	}
	return `Derived from base price - ${roomType}`;
};

const RenderChange: React.FunctionComponent<{ currentPrice: number, submittedPrice: number | undefined }> = ({ currentPrice, submittedPrice }) => {
	if (submittedPrice === undefined || !Number.isFinite(submittedPrice) || !Number.isFinite(currentPrice) || currentPrice === 0) return <span>—</span>;
	const rate = Utilities.roundDouble(((submittedPrice - currentPrice) / currentPrice) * 100, 0);
	if (rate === 0) return null;
	return rate > 0 ? <div className="inline-flex items-center gap-x-0.5 text-green-500">
		<ArrowUpIcon className="h-3 w-3" />
		<span>+{rate}%</span>
	</div> : <div className="inline-flex items-center gap-x-0.5 text-red-500">
		<ArrowDownIcon className="h-3 w-3" />
		<span>{rate}%</span>
	</div>;
};

/**
 * The rooms table of a Price Edit Modal, shared by "Edit Recommended Rates" and "Submit Prices".
 * One column set: Rooms, Availability, Current, New Rate, Change. The master room of each group has a
 * blue row; a change of its New Rate cascades to the derivative rooms through DerivativePriceSynchronizer.
 */
const PriceEditRoomsTable: React.FunctionComponent<PriceEditRoomsTableProps> = ({ title, currency, groups, form, suppressionEpoch }) => {
	const { availabilityDailyState } = React.useContext(AvailabilityDailyStateContext);
	const findAvailability = (roomName: string): string => {
		const item = availabilityDailyState?.current?.items?.find(item => item.roomName.trim().toLowerCase() === roomName.trim().toLowerCase());
		return item?.availability == null ? '-' : `${item.availability}`;
	};

	return <div className="mb-4 divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
		<div className="px-3 py-3">
			<h6 className="text-sm font-semibold text-gray-900">{title}</h6>
		</div>
		<div className="text-sm">
			<div className="w-0 min-w-full max-h-[400px] overflow-x-auto overflow-y-auto">
				<table className="w-full min-w-[600px] table-fixed divide-y divide-gray-200 text-xs">
					<colgroup>
						<col />
						<col className="w-20" />
						<col className="w-16" />
						<col className="w-32" />
						<col className="w-20" />
					</colgroup>
					<thead>
					<tr>
						<th scope="col" className="px-3 py-2 text-left">Rooms</th>
						<th scope="col" className="px-3 py-2 text-right">Availability</th>
						<th scope="col" className="px-3 py-2 text-left">Current</th>
						<th scope="col" className="px-3 py-2 text-left">New Rate</th>
						<th scope="col" className="px-3 py-2 text-left">Change</th>
					</tr>
					</thead>
					{groups.map(group => {
						const roomType = Utilities.snakeCaseToWords(group.roomMappingType);
						const rooms = [...group.rooms].sort((a, b) => Number(b.isAnchor) - Number(a.isAnchor));
						const synchronizable = group.rooms.map(room => ({ isAnchor: room.isAnchor, derivativeRule: room.derivativeRule }));
						const hasAnchor = group.rooms.some(room => room.isAnchor);
						return <tbody key={group.key} className="divide-y divide-gray-200 bg-white">
						{hasAnchor && <form.Subscribe selector={(state: any) => state.values.rates[group.rateIndex].roomPrices}>
							{(roomPrices: { submittedPrice: number | undefined }[]) => <DerivativePriceSynchronizer
								form={form}
								rateIndex={group.rateIndex}
								rooms={synchronizable}
								roomPrices={roomPrices}
								suppressionEpoch={suppressionEpoch} />}
						</form.Subscribe>}
						{rooms.map(room => <tr key={`room-${room.roomId}`} className={room.isAnchor ? 'bg-sky-50' : undefined}>
							<td className="px-3 py-2 font-medium text-gray-700">
								{room.roomName}
								<span className="block text-xs font-light italic text-gray-400">({renderRuleNote(room, currency, roomType)})</span>
							</td>
							<td className="px-3 py-2 text-right text-gray-700">{findAvailability(room.roomName)}</td>
							<td className="whitespace-nowrap px-3 py-2 text-gray-700">
								{Utilities.formatAmount(room.currentPrice, currency, Number.isInteger(room.currentPrice) ? 0 : 2)}
							</td>
							<td className="px-3 py-2 text-gray-500">
								<form.AppField name={`rates[${group.rateIndex}].roomPrices[${room.roomIndex}].submittedPrice`}>
									{(field: any) => <field.NumberField placeholder="Set price" />}
								</form.AppField>
							</td>
							<td className="whitespace-nowrap px-3 py-2 text-gray-500">
								<form.Subscribe selector={(state: any) => state.values.rates[group.rateIndex].roomPrices[room.roomIndex].submittedPrice}>
									{(submittedPrice: number | undefined) => <RenderChange currentPrice={room.currentPrice} submittedPrice={submittedPrice} />}
								</form.Subscribe>
							</td>
						</tr>)}
						</tbody>;
					})}
				</table>
			</div>
		</div>
	</div>;
};

export default PriceEditRoomsTable;
